import { User, UserFilled } from '@ori-ui/icons';
import { Avatar, Button, ButtonBase, styled } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import { useLogin } from '@ori/login';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';

import { useHover, useCustomerInitials } from '../hooks';
import { useTranslations } from '../hooks/useTranslations';
import { MY_PAGES_URL, USER_ICON_DESKTOP, USER_ICON_MOBILE } from './constants';

const SignInLabel = styled(ButtonBase)<{
  disableRipple?: boolean;
}>(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    color: theme.palette.text.primary,
    display: 'block',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    '&:hover, &:focus, &:active': {
      color: theme.palette.grey[600],
      cursor: 'pointer',
    },
  },
}));

const DesktopUserIconButton = styled(Button)(({ theme }) => ({
  display: 'none',
  fontSize: theme.typography.pxToRem(14),
  whiteSpace: 'nowrap',

  lineHeight: '21px',
  minWidth: 'auto',
  fontWeight: theme.typography.fontWeightBold,
  svg: {
    marginLeft: theme.spacing(2),
  },
  [theme.breakpoints.up('md')]: {
    display: 'inline-flex',
  },
}));

const MobileUserIconButton = styled(ButtonBase)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(30),
  color: theme.palette.grey[700],
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const UserAvatar = styled(Avatar)(({ theme }) => ({
  fontSize: `${theme.typography.pxToRem(10)} !important`,
  width: 22,
  height: 22,
  backgroundColor: theme.palette.grey[700],
}));

export const UserSection: FC = () => {
  const [ref, Icon] = useHover(UserFilled, User);
  const { getTestId } = useTestId();
  const { isAnonymousUser } = useToken();
  const { doLogin } = useLogin();
  const { translations } = useTranslations();
  const customerInitials = useCustomerInitials();

  if (!isAnonymousUser) {
    return (
      <>
        <MobileUserIconButton
          ref={ref}
          aria-label={translations.myAccount}
          data-testid={getTestId(USER_ICON_MOBILE)}
        >
          {customerInitials ? (
            <a href={MY_PAGES_URL}>
              <UserAvatar>{customerInitials}</UserAvatar>
            </a>
          ) : null}
        </MobileUserIconButton>
        <DesktopUserIconButton
          href={MY_PAGES_URL}
          startIcon={customerInitials ? <UserAvatar>{customerInitials}</UserAvatar> : null}
          variant="contained"
          data-testid={getTestId(USER_ICON_DESKTOP)}
          color="secondary"
        >
          <span>{translations.myAccount}</span>
        </DesktopUserIconButton>
      </>
    );
  }

  return (
    <>
      <MobileUserIconButton
        ref={ref}
        disableRipple
        aria-label={translations.signIn}
        data-testid={getTestId(USER_ICON_MOBILE)}
        onClick={doLogin}
      >
        <Icon fontSize="inherit" />
      </MobileUserIconButton>
      <SignInLabel
        ref={ref}
        disableRipple
        data-testid={getTestId(USER_ICON_DESKTOP)}
        onClick={doLogin}
      >
        {translations.signIn}
      </SignInLabel>
    </>
  );
};
